/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import colorUtils from 'color';

import AnimateNumber from 'app/components/commons/AnimateNumber';
import StatusIcon from 'app/components/challenge/StatusIcon';

import { hover } from '../../utils/cssUtils';
import Tappable from '../commons/Tappable';
import { isInProgress } from '../../utils/challengeUtils';

type Props = {
  dropped?: boolean,
  id: string,
  isTeam?: boolean,
  name: string,
  onSelectEntity: (entity: Object) => void,
  popularity?: number,
  score: number,
  statusId?: number,
  isSelected?: boolean,
  playerSubmitted: boolean
};

const EntityWrapper = styled.div(
  ({ theme, isSelected }) => ({
    position: 'relative',
    borderColor: theme.color.black,
    borderStyle: 'solid',
    borderWidth: 1,
    fontSize: '0.9rem',
    color: isSelected ? theme.color.white : theme.color.basis1,
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    borderBottomWidth: 1
  }),
  ({ disabled, theme, isSelected }) =>
    !disabled && !isSelected
      ? hover({
          backgroundColor: colorUtils(theme.color.white)
            .darken(0.05)
            .hex()
        })
      : {
          backgroundColor: theme.color.basis1
        }
);

const BarFinal = styled.div(
  {
    display: 'flex',
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 0,
    justifyContent: 'space-between'
  },
  ({ compressed }) =>
    compressed
      ? {
          flexDirection: 'column',
          alignItems: 'flex-end'
        }
      : {
          flexDirection: 'row-reverse',
          alignItems: 'center'
        }
);

const StatusIconWrapper = styled.div(
  {
    color: 'white',
    fontSize: '0.5rem',
    height: '2.2em',
    left: 0,
    padding: 1,
    position: 'absolute',
    top: 0,
    transform: 'rotate(360deg)', // stop jitter effect
    width: '2.2em'
  },
  ({ live, theme }) =>
    live
      ? {
          backgroundImage: `linear-gradient(135deg, ${theme.color.primary} 50%, transparent 50%)`
        }
      : {
          backgroundImage: `linear-gradient(135deg, ${theme.color.basis1} 50%, transparent 50%)`
        }
);

const PopularityWrapper = styled.div(({ compressed }) => ({
  flexGrow: 1,
  maxWidth: compressed ? 50 : 70,
  width: 50,
  fontSize: '0.8rem',
  textAlign: 'center'
}));

const StyledPlayerBarValue = styled.div(
  {
    padding: '6px 2px',
    textAlign: 'center',
    minWidth: 70
  },
  ({ theme, isSubmitted }) =>
    isSubmitted
      ? {
          color: theme.color.white,
          backgroundColor: theme.color.primary
        }
      : {}
);

const PlayerBarName = styled.div(
  {
    flexBasis: 0,
    flexShrink: 1,
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    transition: 'color 0.3s',
    paddingLeft: 10
  },
  ({ theme }) => ({
    fontWeight: theme.fontWeight.demiBold
  }),
  ({ theme, highlight }) =>
    highlight
      ? {
          color: theme.color.primary
        }
      : {}
);

const Wrapper = styled.div({
  marginTop: 6
});

class LeaderboardEntityFilter extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    dropped: false,
    popularity: undefined,
    statusId: undefined,
    isSelected: false,
    isTeam: false
  };

  render() {
    const {
      id,
      isTeam,
      dropped,
      name,
      popularity,
      score,
      statusId,
      isSelected,
      onSelectEntity,
      playerSubmitted
    } = this.props;

    return (
      <Wrapper>
        <Tappable
          component={({ children, ...rest }) => (
            <EntityWrapper isSelected={isSelected} {...rest}>
              {children}
            </EntityWrapper>
          )}
          onClick={() => onSelectEntity({ id, isTeam })}
        >
          <StatusIconWrapper live={isInProgress(statusId)}>
            <StatusIcon statusId={statusId} />
          </StatusIconWrapper>
          <PlayerBarName>{name}</PlayerBarName>
          <BarFinal>
            <StyledPlayerBarValue dropped={dropped} isSubmitted={playerSubmitted}>
              <AnimateNumber format="0.00" number={score} /> pts
            </StyledPlayerBarValue>
            {popularity !== undefined && <PopularityWrapper>{popularity}%</PopularityWrapper>}
          </BarFinal>
        </Tappable>
      </Wrapper>
    );
  }
}

export default LeaderboardEntityFilter;
